import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { EnvConfig, FleetOverview, PromotionConfig, PromotionRequest, Role, TreeResponse } from "../api/types";
import { CATEGORY_LABELS, canaryCategory, findEnvCluster, type CanaryCategory } from "../util/argots";
import { behindLatest } from "../util/versions";
import AlertBadge from "./AlertBadge";
import DataAgeBadge from "./DataAgeBadge";
import MrChip from "./MrChip";
import VersionChip from "./VersionChip";

const MAX_DEPTH = 12; // guard against an accidental dependsOn cycle in the config

interface TreeProps {
    tree: TreeResponse;
    overview: FleetOverview | null;
    promotions: PromotionRequest[];
    pendingEditEnvs: Set<string>;
    role: Role;
    onPromote: (envKey: string) => void;
}

type CategoryFilter = CanaryCategory | "all";

export default function PromotionTree(props: TreeProps) {
    const config = props.tree.config;
    const [category, setCategory] = useState<CategoryFilter>("all");
    const [focusRoot, setFocusRoot] = useState<string | null>(null);

    if (!config) return <div className="empty">No promotion config loaded yet.</div>;

    const childrenMap = new Map<string | null, string[]>();
    const keys = Object.keys(config.envs).sort();
    for (const envKey of keys) {
        const parent = config.envs[envKey]?.dependsOn ?? null;
        const list = childrenMap.get(parent) ?? [];
        list.push(envKey);
        childrenMap.set(parent, list);
    }
    const known = new Set(keys);
    const roots = [...(childrenMap.get(null) ?? [])];
    // Parents referenced but not defined: surface their children as roots so nothing is hidden.
    for (const [parent, list] of childrenMap) {
        if (parent !== null && !known.has(parent)) roots.push(...list);
    }

    const categoriesPresent = new Set(roots.map(canaryCategory));
    const inCategory = roots.filter((r) => category === "all" || canaryCategory(r) === category);
    // A focused root that survives the category filter wins; otherwise show the whole category.
    const visibleRoots = focusRoot && inCategory.includes(focusRoot) ? [focusRoot] : inCategory;

    const pickCategory = (c: CategoryFilter) => {
        setCategory(c);
        if (focusRoot && c !== "all" && canaryCategory(focusRoot) !== c) setFocusRoot(null);
    };

    // The eye toggle only makes sense when there is more than one canary to choose between.
    const showEye = inCategory.length > 1;
    const toggleFocus = (root: string) => setFocusRoot((cur) => (cur === root ? null : root));

    return (
        <>
            <TreeFilter category={category} categoriesPresent={categoriesPresent} onCategory={pickCategory} />
            <div className="tree">
                {visibleRoots.length === 0 ? (
                    <div className="empty">No canaries match this filter.</div>
                ) : (
                    visibleRoots.map((envKey) => (
                        <div className="tree-root" key={envKey}>
                            {showEye ? (
                                <CanaryEye isolated={focusRoot === envKey} envKey={envKey} onClick={() => toggleFocus(envKey)} />
                            ) : null}
                            <TreeNode envKey={envKey} config={config} childrenMap={childrenMap} depth={0} {...props} />
                        </div>
                    ))
                )}
            </div>
        </>
    );
}

function TreeFilter({
    category,
    categoriesPresent,
    onCategory,
}: {
    category: CategoryFilter;
    categoriesPresent: Set<CanaryCategory>;
    onCategory: (c: CategoryFilter) => void;
}) {
    if (categoriesPresent.size <= 1) return null;

    const categories: CategoryFilter[] = ["all", ...([...categoriesPresent].sort() as CanaryCategory[])];
    return (
        <div className="tree-toolbar">
            <div className="fleet-switch">
                {categories.map((c) => (
                    <button key={c} className={`btn ${category === c ? "active" : ""}`} onClick={() => onCategory(c)}>
                        {c === "all" ? "All" : CATEGORY_LABELS[c]}
                    </button>
                ))}
            </div>
        </div>
    );
}

function CanaryEye({ isolated, envKey, onClick }: { isolated: boolean; envKey: string; onClick: () => void }) {
    return (
        <button
            type="button"
            className={`canary-eye icon-btn ${isolated ? "active" : ""}`}
            onClick={onClick}
            aria-pressed={isolated}
            title={isolated ? `Showing only ${envKey} — click to show all` : `Show only ${envKey}`}
        >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle cx="12" cy="12" r="3" fill={isolated ? "currentColor" : "none"} />
            </svg>
        </button>
    );
}

function TreeNode({
    envKey,
    config,
    childrenMap,
    depth,
    ...props
}: TreeProps & { envKey: string; config: PromotionConfig; childrenMap: Map<string | null, string[]>; depth: number }) {
    const kids = depth < MAX_DEPTH ? (childrenMap.get(envKey) ?? []) : [];
    return (
        <div className="tree-node">
            <EnvNode envKey={envKey} config={config} {...props} />
            {kids.length > 0 ? (
                <div className="tree-children">
                    {kids.map((k) => (
                        <TreeNode key={k} envKey={k} config={config} childrenMap={childrenMap} depth={depth + 1} {...props} />
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function EnvNode({
    envKey,
    config,
    tree,
    overview,
    promotions,
    pendingEditEnvs,
    role,
    onPromote,
}: TreeProps & { envKey: string; config: PromotionConfig }) {
    const location = useLocation();
    const env = config.envs[envKey];
    const pin = tree.pins[envKey];
    const parentKey = env?.dependsOn ?? null;
    const parentPin = parentKey !== null ? tree.pins[parentKey] : undefined;
    const latestTag = overview?.latestTags?.components["argocd-ts-cmp"]?.[0];
    const cluster = findEnvCluster(overview, envKey, env);
    const alert = cluster ? overview?.alerts.summaries.find((s) => s.cluster === cluster.cluster) : undefined;
    const promotion = promotions.find((p) => p.envKey === envKey);

    const behindUpstream = behindLatest(pin?.version, parentPin?.version);
    const behindHead = behindLatest(pin?.version, latestTag);
    const behindTitle = behindUpstream
        ? `behind upstream ${parentKey ?? ""} (${parentPin?.version ?? "?"})`
        : behindHead
          ? `behind latest argocd-ts-cmp (${latestTag ?? "?"})`
          : undefined;

    return (
        <div className="env-node-card">
            <div className="env-node-row">
                <Link className="env-key" to={{ pathname: `/env/${envKey}`, search: location.search }}>
                    {envKey}
                </Link>
                {cluster ? <span className="muted env-cluster">{cluster.cluster}</span> : null}
                <VersionChip version={pin?.version} behind={behindUpstream || behindHead} title={behindTitle} />
                <DataAgeBadge fetchedAt={cluster?.fetchedAt ?? tree.fetchedAt} />
                {pendingEditEnvs.has(envKey) ? (
                    <span className="edit-badge" title="An open tree-edit MR touches this env">
                        edit pending
                    </span>
                ) : null}
            </div>
            <div className="env-node-row meta">
                {env ? (
                    <span className="muted" title={windowsTitle(env, config)}>
                        {soakWindowSummary(env)}
                    </span>
                ) : null}
                <AlertBadge summary={alert} />
                {cluster?.grafanaUrl ? (
                    <a className="source-link" href={cluster.grafanaUrl} target="_blank" rel="noopener noreferrer">
                        Grafana
                    </a>
                ) : null}
                {promotion ? <MrChip promotion={promotion} /> : null}
                {role === "promoter" ? (
                    <button className="btn btn-small promote-btn" onClick={() => onPromote(envKey)}>
                        Promote
                    </button>
                ) : null}
            </div>
        </div>
    );
}

function soakWindowSummary(env: EnvConfig): string {
    const parts: string[] = [];
    if (env.soakDays !== undefined) parts.push(`soak ${env.soakDays}d`);
    if (env.windows && env.windows.length > 0) parts.push(`windows ${env.windows.join(", ")}`);
    if (env.delaySeconds !== undefined && env.delaySeconds > 0) parts.push(`delay ${Math.round(env.delaySeconds / 3600)}h`);
    parts.push(env.mergePolicy);
    return parts.join(" · ");
}

function windowsTitle(env: EnvConfig, config: PromotionConfig): string | undefined {
    if (!env.windows || env.windows.length === 0) return undefined;
    return env.windows
        .map((w) => {
            const slot = config.slots[w];
            return slot ? `${w}: ${slot.desc ?? slot.cron} (${slot.tz})` : w;
        })
        .join("\n");
}
